import { getTextareaProps } from '@conform-to/react'
import { invariant } from '@epic-web/invariant'
import { useFetcher } from '@remix-run/react'
import { type VirtualItem, type Virtualizer } from '@tanstack/react-virtual'
import { blurhashToCssGradientString } from '@unpic/placeholder'
import { Image } from '@unpic/react'
import { CircleArrowDown, CircleArrowUp, Loader2 } from 'lucide-react'
import React, { useState, useEffect, memo, useCallback, useRef } from 'react'

import { useSpinDelay } from 'spin-delay'
import {
	decodeImageFormat,
	type ImageOrPlaceholder,
} from '#app/routes/generate+/_layout'
import { type GeneratedImage2 as GeneratedImage } from '#app/routes/resources+/generated-images'
import { type AspectRatio, IMAGE_BREAKPOINTS } from '#app/utils/constants'
import { WORKFLOWS } from '#app/utils/helpers'
import { useImageUrl } from '#app/utils/hooks/useImageUrl'
import { cn } from '#app/utils/misc'
import { useOptionalUser } from '#app/utils/user.js'
import { TextareaField } from './forms'
import { ImageActionButtons } from './image-action-buttons'
import { useSelectionContext } from './image-selection-context'
import { NsfwImageOverlay } from './nsfw-image-overlay'
import { Button } from './ui/button'
import { Icon } from './ui/icon'
import { Skeleton } from './ui/skeleton'

interface TileProps {
	vRow: VirtualItem<Element>
	image: ImageOrPlaceholder
	columnWidth: number
	containerWidth: number
	isSelectedImage?: boolean
	// captionField?: FieldConfig<string> | null
	captionField?: any
	virtualizerRef: Virtualizer<Element, Element>['measureElement']
}

function calculateImageSize(
	image: ImageOrPlaceholder,
	columnWidth: number,
	containerWidth: number,
) {
	const aspectRatio: AspectRatio =
		'isPlaceholder' in image
			? image.format.ratio
			: decodeImageFormat(image.format).ratio
	const imageWidth = Math.floor(containerWidth * (columnWidth / 100))
	const imageHeight = Math.floor(
		imageWidth * (aspectRatio.height / aspectRatio.width),
	)

	return { aspectRatio, imageWidth, imageHeight }
}

export const ImageTile: React.FC<TileProps> = memo(function ImageTile({
	vRow,
	image,
	columnWidth,
	containerWidth,
	isSelectedImage = false,
	captionField,
	virtualizerRef,
}) {
	const user = useOptionalUser()
	const { actualImageUrl } = useImageUrl(
		'isPlaceholder' in image ? null : image,
	)
	const { aspectRatio, imageWidth, imageHeight } = calculateImageSize(
		image,
		columnWidth,
		containerWidth,
	)
	const { handleSelection, enableSelection, handleDialogImageClick } =
		useSelectionContext()

	invariant(handleDialogImageClick, 'handleDialogImageClick is required')
	const selectionFetcher = useFetcher()

	const handleImageSelection = useCallback(() => {
		if (handleSelection && !('isPlaceholder' in image)) {
			handleSelection({ id: image.id }, selectionFetcher)
		}
	}, [handleSelection, image, selectionFetcher])

	if (!imageHeight) return null

	return (
		<div
			onClick={
				'isPlaceholder' in image
					? undefined
					: enableSelection
						? handleImageSelection
						: () => {
								handleDialogImageClick(image, WORKFLOWS.generateImage)
							}
			}
		>
			<div
				ref={virtualizerRef}
				data-index={vRow.index}
				style={{
					position: 'absolute',
					top: 0,
					left: `${vRow.lane * columnWidth}%`,
					width: `${columnWidth}%`,
					height: `${imageHeight + (captionField ? 128 : 0)}px`,
					transform: `translateY(${vRow.start}px)`,
					zIndex: 1,
				}}
				className="pointer-events-auto p-2"
			>
				{'isPlaceholder' in image ? (
					<SkeletonWithCounter />
				) : (
					<TileContent
						image={image}
						aspectRatio={aspectRatio}
						actualImageUrl={actualImageUrl}
						imageWidth={imageWidth}
						imageHeight={imageHeight}
						isSelectedImage={isSelectedImage}
					/>
				)}
				<CaptionField field={captionField} />
			</div>
			{!('isPlaceholder' in image) ? (
				<div
					style={{
						position: 'absolute',
						top: 10,
						left: `${vRow.lane * columnWidth + 0.5}%`,
						width: `${columnWidth - 1}%`,
						height: `${imageHeight + (captionField ? 128 : 0)}px`,
						transform: `translateY(${vRow.start}px)`,
						padding: '0.5rem',
						zIndex: 2,
					}}
					className="pointer-events-none"
				>
					<div className="pointer-events-auto absolute right-0 top-0 z-10 mb-4 flex w-full items-start justify-start gap-4 p-2 transition-all duration-500">
						<ImageActionButtons image={image} />
					</div>
					{user?.id === image.ownerId && !image.nsfw ? (
						<PublishButton image={image} />
					) : null}
				</div>
			) : null}
		</div>
	)
})

interface CaptionFieldProps {
	// field?: FieldConfig<string> | null
	field?: any
}

const CaptionField: React.FC<CaptionFieldProps> = ({ field }) => {
	if (!field) return null

	return (
		<TextareaField
			textareaProps={{
				// ...conform.textarea(field, {
				// 	ariaAttributes: true,
				// }),

				...getTextareaProps(field),
				className: 'h-24 resize-none',
				placeholder: 'Write caption here...',
			}}
			enableScroll
			className="m-0 flex w-full items-start justify-start p-0"
			errors={field.errors}
		/>
	)
}

interface ContentProps {
	image: GeneratedImage
	aspectRatio: AspectRatio
	actualImageUrl: string | null
	imageWidth: number
	imageHeight: number
	isSelectedImage: boolean
}

const TileContent: React.FC<ContentProps> = memo(function TileContent({
	image,
	aspectRatio,
	actualImageUrl,
	imageWidth,
	imageHeight,
	isSelectedImage,
}) {
	const [isImageLoaded, setIsImageLoaded] = useState(false)
	const user = useOptionalUser()
	const { enableSelection } = useSelectionContext()

	return (
		<div className="flex h-full w-full flex-col items-center justify-center gap-2">
			<div
				className={cn(
					'group relative h-full w-full overflow-hidden rounded-lg transition-opacity duration-500',
					'cursor-pointer',
					isSelectedImage &&
						(isImageLoaded || (image.nsfw && !user?.enableNsfw)) &&
						'ring-2 ring-primary',
				)}
				style={{ aspectRatio: `${aspectRatio.width}/${aspectRatio.height}` }}
			>
				<PlaceholderBackground image={image} isImageLoaded={isImageLoaded} />
				{actualImageUrl && (
					<ActualImage
						image={image}
						actualImageUrl={actualImageUrl}
						imageWidth={imageWidth}
						imageHeight={imageHeight}
						aspectRatio={aspectRatio}
						isImageLoaded={isImageLoaded}
						setIsImageLoaded={setIsImageLoaded}
					/>
				)}
				{image.nsfw && !user?.enableNsfw ? <NsfwImageOverlay /> : <></>}
				{isImageLoaded && enableSelection && (
					<SelectionCheckOverlay isSelected={isSelectedImage} />
				)}
			</div>
		</div>
	)
})

// New smaller components
export const PlaceholderBackground: React.FC<{
	image: GeneratedImage
	isImageLoaded: boolean
	className?: string
}> = ({ image, isImageLoaded, className }) => (
	<div
		className={cn('absolute inset-0', className)}
		style={{
			background: blurhashToCssGradientString(image.blurhash),
			transition: 'opacity 0.5s ease-in-out',
			opacity: isImageLoaded ? 0 : 1,
		}}
	/>
)

const ActualImage: React.FC<{
	image: GeneratedImage
	actualImageUrl: string
	imageWidth: number
	imageHeight: number
	aspectRatio: AspectRatio
	isImageLoaded: boolean
	setIsImageLoaded: React.Dispatch<React.SetStateAction<boolean>>
}> = ({
	image,
	actualImageUrl,
	imageWidth,
	imageHeight,
	aspectRatio,
	isImageLoaded,
	setIsImageLoaded,
}) => (
	<Image
		key={image.id}
		loading="eager"
		layout="constrained"
		width={imageWidth}
		// height={imageHeight}
		aspectRatio={aspectRatio.width / aspectRatio.height}
		cdn="bunny"
		className={cn(
			'h-full w-full rounded-lg transition-opacity duration-500',
			isImageLoaded ? 'opacity-100' : 'opacity-0',
			isImageLoaded && 'group-hover:opacity-60',
		)}
		alt=""
		background="transparent"
		breakpoints={IMAGE_BREAKPOINTS}
		src={actualImageUrl}
		onLoad={() => setIsImageLoaded(true)}
	/>
)

const SelectionCheckOverlay: React.FC<{
	isSelected: boolean
}> = ({ isSelected }) => (
	<div
		className={cn(
			'absolute right-0 top-0 z-10 mb-4 flex w-full items-start justify-start gap-4 p-2',
		)}
	>
		<div
			className={cn(
				'flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-2',
				isSelected
					? 'border-background bg-primary text-background'
					: 'border-background bg-foreground text-background opacity-50',
				'group-hover:border-background group-hover:bg-primary group-hover:text-background group-hover:opacity-100',
			)}
		>
			<Icon className="text-body-sm text-background" name="check" />
		</div>
	</div>
)

// Updated SkeletonWithCounter component
const SkeletonWithCounter: React.FC = () => {
	const [time, setTime] = useState(0)
	const intervalRef = useRef<NodeJS.Timeout | null>(null)

	useEffect(() => {
		const startTime = Date.now()
		intervalRef.current = setInterval(() => {
			setTime(Date.now() - startTime)
		}, 10) // Update every 10ms for smooth millisecond display

		return () => {
			if (intervalRef.current) clearInterval(intervalRef.current)
		}
	}, [])

	const formatTime = (ms: number) => {
		const seconds = Math.floor(ms / 1000)
		const milliseconds = Math.floor((ms % 1000) / 10)
		return `${seconds}.${milliseconds.toString().padStart(2, '0')}s`
	}

	return (
		<div className="relative h-full w-full">
			<Skeleton className="h-full w-full rounded-lg bg-accent" />
			<div className="absolute bottom-2 right-2 overflow-hidden rounded-full bg-gradient-to-r from-black/40 to-black/60 shadow-lg backdrop-blur-md">
				<span className="flex w-20 items-center justify-center px-3 py-1.5 text-right font-mono text-xs tracking-wider text-foreground antialiased">
					{formatTime(time)}
				</span>
			</div>
		</div>
	)
}

interface PublishButtonProps {
	image: Pick<GeneratedImage, 'id' | 'isPublic'>
}

export function PublishButton({ image }: PublishButtonProps) {
	const fetcher = useFetcher<{
		success: boolean
		image: Pick<GeneratedImage, 'id' | 'isPublic'>
	}>()
	const [loadingState, setLoadingState] = useState<
		'publishing' | 'unpublishing' | null
	>(null)

	// Get the current isPublic state, prioritizing the fetcher data
	const isPublic = fetcher.data?.success
		? fetcher.data.image.isPublic
		: image.isPublic

	const isSubmitting = fetcher.state !== 'idle'
	const showSpinner = useSpinDelay(isSubmitting, {
		delay: 150,
		minDuration: 500,
	})

	// Set the loading state when submission starts
	useEffect(() => {
		if (isSubmitting && !loadingState) {
			setLoadingState(isPublic ? 'unpublishing' : 'publishing')
		} else if (!isSubmitting) {
			setLoadingState(null)
		}
	}, [isSubmitting, isPublic, loadingState])

	// Determine button variant based on loading state or current public status
	const buttonVariant = showSpinner
		? loadingState === 'publishing'
			? 'outline'
			: 'destructive'
		: !isPublic
			? 'outline'
			: 'destructive'

	return (
		<fetcher.Form
			method="post"
			action={`/resources/publish-image?imageId=${image.id}`}
			className="absolute bottom-8 right-2 z-10"
			onSubmit={(e) => e.stopPropagation()}
		>
			<Button
				variant={buttonVariant}
				type="submit"
				name="publish"
				disabled={isSubmitting}
				className={cn(
					'pointer-events-auto relative z-10 flex items-center gap-2 transition-colors disabled:opacity-80',
				)}
				onClick={(e) => e.stopPropagation()}
			>
				{showSpinner ? (
					<>
						<Loader2 className="h-4 w-4 animate-spin text-foreground" />
						{loadingState === 'publishing' ? 'Saving...' : 'Hiding...'}
					</>
				) : (
					<>
						{isPublic ? (
							<>
								Unpublish
								<CircleArrowDown className="h-4 w-4" />
							</>
						) : (
							<>
								Publish
								<CircleArrowUp className="h-4 w-4" />
							</>
						)}
					</>
				)}
			</Button>
		</fetcher.Form>
	)
}
